import { useState } from 'react';
import { doc, updateDoc, serverTimestamp, addDoc, collection } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { ERROR_TYPES } from '../../components/Modal/ErrorModal';
import { trackError } from '../../utils';
import { useTranslation } from 'react-i18next';
import { getLanguageByCode } from '../../constants';
import { AI_API_URL } from '../../constants';

export const useErrorHandler = (userId, setAnalyzeLoading = null, setProgressModal = null, history = []) => {
  const { i18n } = useTranslation();
  
  // 에러 모달 관련 상태
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorType, setErrorType] = useState(ERROR_TYPES.UNKNOWN_ERROR);
  const [errorMessage, setErrorMessage] = useState('');
  const [retryAction, setRetryAction] = useState(null);
  const [failedHistoryId, setFailedHistoryId] = useState(null);

  // 에러 모달 표시 함수
  const showErrorModal = (type, message = '', retry = null, historyId = null) => {
    setErrorType(type);
    setErrorMessage(message);
    setRetryAction(() => retry);
    setFailedHistoryId(historyId);
    setErrorModalOpen(true);
    
    // ✅ 에러 모달 표시 시 analyzeLoading 상태 해제 (로딩 스피너 제거)
    if (setAnalyzeLoading) {
      setAnalyzeLoading(false);
    }
    // ✅ 에러 모달 표시 시 progressModal도 닫기 (중복 모달 방지)
    if (setProgressModal) {
      setProgressModal(prev => ({ ...prev, open: false }));
    }
  };

  // 에러 모달 닫기 함수
  const closeErrorModal = async () => {
    // DUPLICATE_VIDEO 에러인 경우 히스토리로 이동
    if (errorType === ERROR_TYPES.DUPLICATE_VIDEO && failedHistoryId) {
      console.log('[중복 영상] 기존 히스토리로 이동:', failedHistoryId);
      setErrorModalOpen(false);
      setRetryAction(null);
      setFailedHistoryId(null);
      
      // 현재 페이지가 analyze 페이지가 아닌 경우에만 이동
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith('/analyze/')) {
        window.location.href = `/analyze/${failedHistoryId}`;
      } else {
        // analyze 페이지에서는 현재 페이지를 해당 히스토리로 변경
        window.location.href = `/analyze/${failedHistoryId}`;
      }
      return;
    }
    
    setErrorModalOpen(false);
    
    // 에러로 실패한 히스토리가 있으면 삭제 처리
    if (failedHistoryId) {
      try {
        console.log('[에러 취소] 실패한 히스토리 삭제:', failedHistoryId);
        
        // 백엔드 API로 완전 삭제
        const response = await fetch(`${AI_API_URL}/api/delete-history/${failedHistoryId}`, {
          method: 'DELETE',
          headers: { 'user-id': userId }
        });
        
        if (!response.ok) {
          console.error('[에러 취소] 히스토리 삭제 API 실패:', response.status);
        }
        
        // 모바일 기기 체크
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                         window.innerWidth <= 768;
        
        // 현재 페이지 확인하여 이동 처리
        const currentPath = window.location.pathname;
        
        if (isMobile) {
          // 모바일에서는 무조건 홈 페이지로 이동
          console.log('[에러 취소] 모바일 기기 - 홈 페이지로 이동');
          window.location.href = '/';
        } else if (currentPath.startsWith('/analyze/')) {
          // 데스크톱에서는 analyze 페이지에서 에러 발생한 경우 이전 페이지로 이동
          console.log('[에러 취소] 데스크톱 - 이전 페이지로 이동');
          window.history.back();
        }
        
      } catch (error) {
        console.error('[에러 취소] 히스토리 삭제 실패:', error);
      }
    }
    
    setRetryAction(null);
    setFailedHistoryId(null);
  };

  // 재시도 함수
  const handleRetry = async () => {
    // 에러로 실패한 히스토리가 있으면 삭제 처리 후 새로운 분석 시작
    if (failedHistoryId) {
      try {
        console.log('[에러 재시도] 실패한 히스토리 삭제 후 새로 생성:', failedHistoryId);
        
        // 1. 기존 히스토리에서 영상 정보 가져오기
        const failedHistory = history.find(h => h.id === failedHistoryId);
        if (!failedHistory) {
          console.error('[에러 재시도] 실패한 히스토리를 찾을 수 없음:', failedHistoryId);
          closeErrorModal();
          return;
        }
        
        const { url, videoId, title, youtubeTitle } = failedHistory;
        
        // 2. 기존 히스토리 삭제
        const deleteResponse = await fetch(`${AI_API_URL}/api/delete-history/${failedHistoryId}`, {
          method: 'DELETE',
          headers: { 'user-id': userId }
        });
        
        if (!deleteResponse.ok) {
          console.error('[에러 재시도] 히스토리 삭제 API 실패:', deleteResponse.status);
        }
        
        // 3. 새로운 히스토리 생성
        // 현재 언어 정보 가져오기
        const currentLanguage = getLanguageByCode(i18n.language);
        
        const newVideo = {
          url: url,
          videoId: videoId,
          title: title || youtubeTitle,
          youtubeTitle: youtubeTitle || title,
          customTitle: title || youtubeTitle,
          summary: "",
          chunk_summaries: [],
          chapters: [],
          transcript: "",
          subtitle: "",
          status: 'analyzing',
          userId: userId,
          lastUpdated: serverTimestamp(),
          createdAt: serverTimestamp(),
          qa: [],
          questions: [],
          projectId: null,
          language: i18n.language,
          languageName: currentLanguage.nativeName
        };
        
        const newDocRef = await addDoc(collection(db, "users", userId, "history"), newVideo);
        const newHistoryId = newDocRef.id;
        
        console.log('[에러 재시도] 새로운 히스토리 생성 완료:', newHistoryId);
        
        // 4. 에러 모달 닫기
        setErrorModalOpen(false);
        setRetryAction(null);
        setFailedHistoryId(null);
        
        // 5. 현재 페이지 확인하여 적절한 처리
        const currentPath = window.location.pathname;
        
        if (currentPath.startsWith('/analyze/')) {
          // analyze 페이지에서 에러 발생한 경우 새로운 히스토리 페이지로 이동
          window.location.href = `/analyze/${newHistoryId}`;
        } else if (currentPath === '/') {
          // 홈페이지에서 에러 발생한 경우 새로운 히스토리 페이지로 이동
          window.location.href = `/analyze/${newHistoryId}`;
        }
        
      } catch (error) {
        console.error('[에러 재시도] 처리 중 오류:', error);
        closeErrorModal();
      }
    } else {
      // 기존 로직: retryAction이 있으면 실행
      if (retryAction) {
        setErrorModalOpen(false);
        try {
          await retryAction();
        } catch (error) {
          console.error('[재시도 실패]:', error);
          // 재시도도 실패한 경우 다시 에러 모달 표시
          showErrorModal(ERROR_TYPES.UNKNOWN_ERROR, '재시도 중 오류가 발생했습니다.');
        }
      } else {
        closeErrorModal();
      }
    }
  };

  return {
    // 상태
    errorModalOpen,
    errorType,
    errorMessage,
    failedHistoryId,
    
    // 함수
    showErrorModal,
    closeErrorModal,
    handleRetry,
  };
}; 